import React, { createContext, useContext, useEffect, useState, useRef } from "react";

const ThemeContext = createContext({ theme: "light", setTheme: () => {} });

const applyTheme = (mode) => {
  const root = document.documentElement;
  // mode: "light" | "dark" | "system"
  let effective = mode;
  if (mode === "system") {
    effective = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
};

export const ThemeProvider = ({ children }) => {
  // Light is the default for all users (per launch spec).
  // Preference: localStorage > "light"; persist to server when user is logged in.
  const [theme, setThemeState] = useState(() => localStorage.getItem("ws_theme") || "light");

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem("ws_theme", theme);
    const onSync = () => {
      const next = localStorage.getItem("ws_theme") || "light";
      if (next !== theme) setThemeState(next);
    };
    window.addEventListener("ws_theme_synced", onSync);
    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme("system");
      mq.addEventListener?.("change", handler);
      return () => {
        mq.removeEventListener?.("change", handler);
        window.removeEventListener("ws_theme_synced", onSync);
      };
    }
    return () => window.removeEventListener("ws_theme_synced", onSync);
  }, [theme]);

  // Persist to user profile when logged in (best-effort, debounced).
  const persistTimerRef = useRef(null);
  const setTheme = (next) => {
    setThemeState(next);
    const token = localStorage.getItem("ws_token");
    if (!token) return;
    clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const backend = process.env.REACT_APP_BACKEND_URL;
      fetch(`${backend}/api/auth/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ theme: next }),
      }).catch(() => { /* best effort */ });
    }, 400);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
